import { watch, type WatchStopHandle } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/useAuthStore'
import { useWhatsAppNotificationStore } from '@/stores/useWhatsAppNotificationStore'
import { useWhatsAppCoordinator } from '@/components/whatsapp/stores/useWhatsAppCoordinator'
import { UserRole } from '@/components/whatsapp/types/enums'
import { WHATSAPP_CONFIG } from '@/components/whatsapp/services/constants'

export const useWhatsAppBackground = () => {
  const authStore = useAuthStore()
  const notificationStore = useWhatsAppNotificationStore()
  const coordinator = useWhatsAppCoordinator()

  const { unreadChatsCount } = storeToRefs(coordinator.chatStore)
  let stopUnreadCountSync: WatchStopHandle | null = null
  let backgroundStartedAt: number | null = null
  let lifecycleListenersBound = false

  const recoverConnection = async (force = false) => {
    if (!authStore.isAuthenticated || !authStore.userId || !navigator.onLine) return

    const backgroundDuration = backgroundStartedAt === null ? 0 : Date.now() - backgroundStartedAt
    backgroundStartedAt = null
    const wasSuspended = backgroundDuration >= WHATSAPP_CONFIG.BACKGROUND_RECONNECT_THRESHOLD_MS

    if (!force) {
      if (coordinator.webSocketStore.isConnected && !wasSuspended) return

      // Haber estado en segundo plano no rompe el socket: lo que el navegador
      // estrangula son los timers del heartbeat, no la conexión. Reconstruir a
      // ciegas cortaba un socket sano y encendía el aviso de reconexión sin que
      // el agente hubiera perdido nada, así que primero se le pregunta al
      // backend y solo se reconstruye si no contesta.
      if (coordinator.webSocketStore.isConnected) {
        const isAlive = await coordinator.webSocketStore.probeConnection()
        if (isAlive) return
      }
    }

    try {
      await coordinator.webSocketStore.manualReconnect()
    } catch (error) {
      console.warn('[WhatsApp WS] No fue posible recuperar la conexión al reactivar la pestaña:', error)
    }
  }

  const handleVisibilityChange = () => {
    if (document.hidden) {
      backgroundStartedAt = Date.now()
      return
    }
    void recoverConnection()
  }

  const handlePageShow = (event: PageTransitionEvent) => void recoverConnection(event.persisted)
  const handleFocus = () => void recoverConnection()
  const handleOnline = () => void recoverConnection(true)

  const bindLifecycleListeners = () => {
    if (lifecycleListenersBound) return
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pageshow', handlePageShow)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('online', handleOnline)
    lifecycleListenersBound = true
  }

  const unbindLifecycleListeners = () => {
    if (!lifecycleListenersBound) return
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('pageshow', handlePageShow)
    window.removeEventListener('focus', handleFocus)
    window.removeEventListener('online', handleOnline)
    lifecycleListenersBound = false
    backgroundStartedAt = null
  }

  const initializeBackground = async () => {
    if (!authStore.userId || notificationStore.isInitialized) {
      return
    }

    try {
      await coordinator.initialize(authStore.userId, UserRole.OPERATOR)
      notificationStore.setInitialized(true)
      bindLifecycleListeners()

      if (!stopUnreadCountSync) {
        stopUnreadCountSync = watch(
          unreadChatsCount,
          (newCount) => {
            notificationStore.setUnreadCount(newCount)
          },
          { immediate: true },
        )
      }
    } catch (error) {
      console.error('Error initializing WhatsApp background:', error)
    }
  }

  const disconnectBackground = () => {
    unbindLifecycleListeners()
    stopUnreadCountSync?.()
    stopUnreadCountSync = null
    coordinator.disconnect()
    notificationStore.setInitialized(false)
    notificationStore.resetUnreadCount()
  }

  return {
    initializeBackground,
    disconnectBackground,
  }
}
